import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ReactiveFormsModule } from "@angular/forms";
import { InterviewState, Question, QuestionGroup } from "@bequeathed/ngx-services";
import { LoggerService } from "@bequeathed/ngx-app-core";
import { BaseControl } from "../base-control/base-control.component";
import { CheckboxControl } from "../checkbox-control/checkbox-control.component";
import { CurrencyControl } from "../currency-control/currency-control.component";
import { DateControl } from "../date-control/date-control.component";
import { DisplayControl } from "../display-control/display-control.component";
import { DropdownControl } from "../dropdown-control/dropdown-control.component";
import { EmailControl } from "../email-control/email-control.component";
import { GroupControl } from "../group-control/group-control.component";
import { NumberControl } from "../number-control/number-control.component";
import { PercentageControl } from "../percentage-control/percentage-control.component";
import { PostcodeControl } from "../postcode-control/postcode-control.component";
import { RadioButtonControl } from "../radio-button-control/radio-button-control.component";
import { TextControl } from "../text-control/text-control.component";

const LOG: LoggerService = LoggerService.get('CharityGiftGroupControl');

/**
 * Charity-specific group control that extends the standard GroupControl with:
 *
 * 1. A "shared question" (e.g. index-linked) that is asked once but its value is
 *    applied to every group entry on save.
 * 2. Per-charity deselection — the user can untick individual charities without
 *    navigating backwards to the charity selection question.
 *
 * The shared question is identified by a configurable suffix (default: "index_linked").
 * It is extracted from each group's question list and rendered once below all groups.
 * On save, the shared answer is injected into every active (non-deselected) group.
 *
 * @author Generated
 */
@Component({
    standalone: true,
    selector: 'charity-gift-group-control',
    templateUrl: './charity-gift-group-control.component.html',
    styleUrl: './charity-gift-group-control.component.sass',
    imports: [
        // Angular
        ReactiveFormsModule,
        // Bequeathed
        CheckboxControl,
        CurrencyControl,
        DisplayControl,
        DropdownControl,
        EmailControl,
        NumberControl,
        PercentageControl,
        PostcodeControl,
        RadioButtonControl,
        TextControl,
        DateControl
    ],
    encapsulation: ViewEncapsulation.None
})
export class CharityGiftGroupControl extends GroupControl implements OnInit {

    /** Suffix used to identify the shared question (asked once, applied to all groups) */
    sharedQuestionSuffix = 'index_linked';

    /** The shared question extracted from the first group — rendered once globally */
    sharedQuestion: Question | undefined;

    /** Tracks which group indexes have been deselected by the user */
    deselectedIndexes = new Set<number>();

    override ngOnInit() {
        this.baseForm.questionControl = this;
        this.baseForm.questionControls = [];

        this.groups = [];
        this.deselectedIndexes = new Set();

        // Extract the shared question from the questions template (it's HIDDEN in the template)
        const sharedQuestionTemplate = this.question.questions?.find(q => q.id?.endsWith(this.sharedQuestionSuffix));
        if (sharedQuestionTemplate) {
            this.sharedQuestion = structuredClone(sharedQuestionTemplate);
            // Override type from HIDDEN to RADIO for rendering (it has acceptedValues Yes/No)
            if (this.sharedQuestion!.type === 'HIDDEN' && this.sharedQuestion!.acceptedValues) {
                this.sharedQuestion!.type = 'RADIO';
            }
        }

        if (this.question.source || (this.question.groupedQuestions && this.question.groupedQuestions.size > 0)) {
            this.question.groupedQuestions?.forEach((questions: Question[], index: number) => {
                const filteredQuestions = questions.filter((q: Question) =>
                    !q.id?.endsWith('changed_mind') && !q.id?.endsWith(this.sharedQuestionSuffix)
                );

                // Remove the shared question from each group's rendered questions
                const groupQuestions = filteredQuestions.filter(q => q.type !== 'HIDDEN');

                this.groups.push(new QuestionGroup({
                    id: this.question.id,
                    name: groupQuestions.find((q: Question) => q.type === 'DISPLAY')?.userResponse,
                    questions: groupQuestions,
                    index: index,
                    adding: false,
                    editing: false,
                    locked: false,
                }));
            });

            // If source is set but groupedQuestions is empty (backend hasn't populated yet),
            // fall through to addGroup to avoid a blank page
            if (this.groups.length === 0 && this.question.questions?.length) {
                LOG.debug('init', 'Source set but no grouped questions populated — falling back to addGroup');
                this.addGroup();
            } else {
                LOG.debug('init', `Created ${this.groups.length} charity gift groups`);
            }
        } else {
            this.addGroup();
        }
    }

    questionHasValidationError(questionId: string) {
        return this.baseForm.interviewState()?.errors?.validations && this.baseForm.interviewState()!.errors!.validations![questionId]
    }

    /** Returns true if any question within the given group has a validation error */
    groupHasValidationError(group: QuestionGroup): boolean {
        const validations = this.baseForm.interviewState()?.errors?.validations;
        if (!validations) return false;
        return this.getDisplayQuestions(group).some(q => !!validations[q.id!]);
    }
    
    /** Returns only the groups that haven't been deselected */
    get activeGroups(): QuestionGroup[] {
        return this.groups.filter(g => !this.deselectedIndexes.has(g.index!));
    }

    /** Returns groups that have been deselected */
    get deselectedGroups(): QuestionGroup[] {
        return this.groups.filter(g => this.deselectedIndexes.has(g.index!));
    }

    /** Returns the renderable questions for a group, excluding the shared question */
    getDisplayQuestions(group: QuestionGroup): Question[] {
        return (group.questions || []).filter(q =>
            !q.id?.endsWith(this.sharedQuestionSuffix) && q.type !== 'HIDDEN'
        );
    }

    /** Toggle a charity group's selection state */
    toggleGroup(group: QuestionGroup): void {
        if (this.deselectedIndexes.has(group.index!)) {
            this.deselectedIndexes.delete(group.index!);
            LOG.debug('toggleGroup', `Re-selected group: ${group.name}[${group.index}]`);
        } else {
            this.deselectedIndexes.add(group.index!);
            LOG.debug('toggleGroup', `Deselected group: ${group.name}[${group.index}]`);
        }
    }

    /** Check whether a group is currently deselected */
    isDeselected(group: QuestionGroup): boolean {
        return this.deselectedIndexes.has(group.index!);
    }

    override async save(group: QuestionGroup, lockGroup = true): Promise<boolean> {
        // Skip validation for deselected groups
        if (this.deselectedIndexes.has(group.index!)) {
            return true;
        }
        return super.save(group, lockGroup);
    }

    override saveAndContinue(event: any): void {
        // Only save active groups
        this.activeGroups.forEach((group: QuestionGroup) => this.save(group, false));
        this.onEnter(event);
    }

    override applyValue(state: InterviewState): void {
        if (this.changedMindControl?.control.value[0]) {
            // Handle changed_mind the same as parent
            const currentQuestion = state.currentQuestion;
            // @ts-expect-error only after change_mind question
            state.currentQuestion = currentQuestion!.questions!.find((q: Question) => {
                if (q.id?.endsWith('changed_mind')) {
                    q.userResponse = this.changedMindControl!.question.acceptedValues.keys().next().value;
                    return q;
                }
            });
        } else {
            // Build grouped questions only from active (non-deselected) groups
            const activeGroups = this.activeGroups;

            if (!this.question.source && (!this.editingResponse || this.question.groupedQuestions!.size < activeGroups.length)) {
                let index = 0;
                const questionAndGroupMaps: { originalIndex: number | undefined; index: number; group: QuestionGroup; controls: BaseControl[] }[] = [];
                for (const group of activeGroups) {
                    const map = { originalIndex: group.index, index, group, controls: [] as BaseControl[] };
                    this.baseForm.questionControls
                        .filter((control: BaseControl) => control.groupIndex === group.index)
                        .forEach((control: BaseControl) => map.controls.push(control));
                    questionAndGroupMaps.push(map);
                    index++;
                }
                questionAndGroupMaps.forEach((groupMap) => {
                    groupMap.group.index = groupMap.index;
                    groupMap.controls.forEach((control: BaseControl) => { control.groupIndex = groupMap.index; });
                    this.addGroupedQuestions(groupMap.group);
                });
            }

            // Apply the shared question's answer to each active group
            const sharedAnswer = this.getSharedQuestionAnswer();

            activeGroups.forEach((group: QuestionGroup) => {
                this.baseForm.questionControls
                    .filter((control: BaseControl) => control.groupIndex === group.index)
                    .forEach((control: BaseControl) => {
                        const question = group.questions!.filter((q: Question) => q.id === control.questionId)[0];
                        control.applyValue(question);
                    });

                // Inject the shared question value into this group's questions
                if (sharedAnswer !== undefined && this.sharedQuestion) {
                    const sharedId = this.buildSharedQuestionId(group.index!);
                    const existingShared = group.questions!.find(q => q.id === sharedId);
                    if (existingShared) {
                        existingShared.userResponse = sharedAnswer;
                        existingShared.calculatedResponse = sharedAnswer;
                    } else {
                        group.questions!.push(new Question({
                            id: sharedId,
                            type: this.sharedQuestion.type,
                            userResponse: sharedAnswer,
                            calculatedResponse: sharedAnswer,
                        }));
                    }
                }
            });

            // Reset the grouped questions map to only include active groups
            this.question.groupedQuestions = new Map<number, Question[]>();
            activeGroups.forEach((group: QuestionGroup) => {
                this.question.groupedQuestions!.set(group.index!, group.questions!);
            });

            state.currentQuestion!.groupedQuestions = this.question.groupedQuestions;
            LOG.debug('applyValue', `Applied values from ${activeGroups.length} active charity gift groups`);
        }
    }

    /** Get the current value of the shared question control */
    private getSharedQuestionAnswer(): string | undefined {
        if (!this.sharedQuestion) return undefined;

        const sharedControl = this.baseForm.questionControls.find(
            c => c.questionId === this.sharedQuestion!.id
        );
        return sharedControl?.modelValue ?? sharedControl?.control?.value;
    }

    /** Build the full question ID for the shared question in a specific group */
    private buildSharedQuestionId(groupIndex: number): string {
        // e.g. charity_gifts.cash.preferred.gifts[0].cash_gift_preferred_index_linked
        const baseSuffix = this.sharedQuestion!.id!.replace(/.*\[\d+\]\./, '').replace(/.*\./, '');
        return `${this.question.id}[${groupIndex}].${baseSuffix}`;
    }
}
